import React, { useState, useEffect, useRef } from "react";
import {
  Stage,
  LocalStageStream,
  SubscribeType,
  StageEvents,
  ConnectionState,
  StreamType,
} from "amazon-ivs-web-broadcast";
import { Box, Container } from "@mui/material";
import moment from "moment";
import {
  getFromLocalStorage,
  removeFromLocalStorage,
  setInToLocalStorage,
} from "../../../utils/webStorage";
import userService from "../../../services/user.service";
import { useSelector, useDispatch } from "react-redux";
import {
  addIVSStageDetails,
  setAvsChannelList,
  setCurrentStageInformation,
} from "../../../Redux/Actions/VideoStreamingActions";
import {
  openFailDialog,
  openSuccessPopup,
} from "../../../Redux/Actions/PopupActions";
import PrimaryStreamer from "./PrimaryStreamer";
import StreamingGridView from "./StreamingGridView";
import StreamAVController from "./StreamAVController";
import { FaDotCircle } from "react-icons/fa";
import styles from "./IPCameraVideoStreaming/IPCameraVideoStreaming.module.css";

const RealTimeStreaming = ({ setPage }) => {
  const [stageName, setStageName] = useState("");
  const [token, setToken] = useState("");
  const [connected, setConnected] = useState(false);
  const [joining, setJoining] = useState(false);
  const [stopBtnLoading, setStopBtnLoading] = useState(false);
  const [stage, setStage] = useState(null);
  const [participants, setParticipants] = useState([]);
  const schoolId = getFromLocalStorage("schoolId");
  const schoolName = getFromLocalStorage("schoolName");
  const userId = getFromLocalStorage("currentUserId");
  //put letter in centralize file
  const savedStream = JSON.parse(localStorage.getItem("livestreamData"));
  const [micEnabled, setMicEnabled] = useState(true);
  const [cameraEnabled, setCameraEnabled] = useState(true);
  const cameraTrackRef = useRef(null);
  const micTrackRef = useRef(null);
  const dispatch = useDispatch();
  const videoStreamAvsChannelList = useSelector(
    (state) => state?.videoStreamData?.videoStreamAvsChannelList
  );
  const videoScheduleInformation = useSelector(
    (state) => state?.videoScheduleInformation
  );
  const primaryChannel = videoStreamAvsChannelList.find(
    (ele) => ele.isPrimaryChannel
  );
  const isPrimaryUser =
    !primaryChannel || primaryChannel.userId?.toString() === userId;
  const currentSeason = useSelector(
    (state) => state.loggedInUser?.userProfile?.currentSeason
  );
  const currentChannel = useSelector(
    (state) => state?.videoStreamData?.currentChannel
  );

  // useEffect(async () => {
  //   if (savedStream) {
  //     const { token, stageArn } = savedStream;
  //     // Check if stage still exists or is live:
  //     const response = await userService.checkStageStatus(stageArn);

  //     if (response.data.data.exists) {
  //       // Join using saved token
  //       joinStage(token);
  //     } else {
  //       // Stage ended – clear storage
  //       localStorage.removeItem("livestreamData");
  //     }
  //   }
  // }, []);

  useEffect(() => {
    return () => {
      if (stage) {
        stage.leave();
      }
    };
  }, [stage]);

  useEffect(() => {
    setAvsChannelList(dispatch, schoolId);
  }, [currentChannel]);

  const getCamera = async () => {
    return navigator.mediaDevices.getUserMedia({
      video: { width: 1280, height: 720 },
      audio: false,
    });
  };

  const getMic = async () => {
    return navigator.mediaDevices.getUserMedia({ video: false, audio: true });
  };

  const joinStage = async (participantToken) => {
    if (connected || joining || !participantToken) return;
    setJoining(true);

    try {
      const localCamera = await getCamera();
      const localMic = await getMic();

      const cameraTrack = localCamera.getVideoTracks()[0];
      const micTrack = localMic.getAudioTracks()[0];

      cameraTrackRef.current = cameraTrack;
      micTrackRef.current = micTrack;

      const cameraStream = new LocalStageStream(cameraTrack);
      const micStream = new LocalStageStream(micTrack);

      const strategy = {
        stageStreamsToPublish: () => [cameraStream, micStream],
        shouldPublishParticipant: () => true,
        shouldSubscribeToParticipant: () => SubscribeType.AUDIO_VIDEO,
      };

      const stageInstance = new Stage(participantToken, strategy);

      stageInstance.on(StageEvents.STAGE_CONNECTION_STATE_CHANGED, (state) => {
        setConnected(state === ConnectionState.CONNECTED);
        if (state !== ConnectionState.CONNECTED) {
          setJoining(false);
          setParticipants([]);
        }
      });

      stageInstance.on(StageEvents.STAGE_PARTICIPANT_JOINED, (participant) => {
        setParticipants((prev) => {
          const alreadyExists = prev.find((p) => p.id === participant.id);
          if (alreadyExists) return prev;
          return [...prev, { ...participant, videoStreams: [] }];
        });
      });

      stageInstance.on(StageEvents.STAGE_PARTICIPANT_LEFT, (participant) => {
        setParticipants((prev) => prev.filter((p) => p.id !== participant.id));
      });

      stageInstance.on(
        StageEvents.STAGE_PARTICIPANT_STREAMS_ADDED,
        (participant, streams) => {
          setParticipants((prev) =>
            prev.map((p) => {
              if (p.id !== participant.id) return p;

              const existingTrackIds = new Set(
                (p.videoStreams || []).map((s) => s.mediaStreamTrack.id)
              );

              const newVideoStreams = streams
                .filter((s) => s.streamType === StreamType.VIDEO)
                .filter((s) => !existingTrackIds.has(s.mediaStreamTrack.id));

              return {
                ...p,
                videoStreams: [...(p.videoStreams || []), ...newVideoStreams],
              };
            })
          );
        }
      );

      stageInstance.on(
        StageEvents.STAGE_PARTICIPANT_STREAMS_REMOVED,
        (participant, streams) => {
          setParticipants((prev) =>
            prev.map((p) => {
              if (p.id !== participant.id) return p;

              const removedTrackIds = new Set(
                streams.map((s) => s.mediaStreamTrack.id)
              );

              const updatedStreams = (p.videoStreams || []).filter(
                (s) => !removedTrackIds.has(s.mediaStreamTrack.id)
              );

              return {
                ...p,
                videoStreams: updatedStreams,
              };
            })
          );
        }
      );

      await stageInstance.join();
      setStage(stageInstance);
      setJoining(false);
    } catch (err) {
      setJoining(false);
      setConnected(false);
    }
  };

  const handleStartStreaming = async () => {
    try {
      setJoining(true);

      const currentStreamDetail = videoStreamAvsChannelList.find(
        (ele) => ele?.userId == userId.toString()
      );

      const formatChannelName = (cameraAngle, schoolName) =>
        `${cameraAngle.split(" ").join("_")}_${schoolName
          .split(" ")
          .join("_")}_${moment().format("YYYY-MM-DD")}`.toUpperCase();

      const getStreamingStartTime = () =>
        moment(new Date()).format("YYYY-MM-DD HH:mm:ss");

      // CASE 1: Reuse saved stream
      if (
        currentStreamDetail &&
        savedStream?.stageArn === currentStreamDetail?.channelArn
      ) {
        joinStage(savedStream.token);
        setJoining(false);
        return;
      }

      const cameraAngle = stageName;
      const channelName = formatChannelName(cameraAngle, schoolName);
      const streamingStartTime = getStreamingStartTime();

      // CASE 2: Secondary joins an existing primary stream
      if (videoStreamAvsChannelList?.length !== 0 && !currentStreamDetail) {
        const primaryChannel = videoStreamAvsChannelList.find(
          (ele) => ele.isPrimaryChannel
        );
        if (!primaryChannel) throw new Error("Primary channel not found");

        const {
          eventType,
          eventName,
          practiceName,
          seasonType,
          scheduleId,
          seasonId,
          playlistId,
          isPractice,
          opponentSchoolId,
          playlistName,
          weekName,
          channelArn,
          userId: primaryUserId,
        } = primaryChannel;

        const response = await userService.generatePubSubToken(
          userId,
          channelArn
        );
        const token = response.data.data.token;
        joinStage(token);
        setToken(token);

        setInToLocalStorage(
          "livestreamData",
          JSON.stringify({
            token: token,
            stageArn: channelArn,
            timestamp: Date.now(),
          })
        );

        addIVSStageDetails(dispatch, {
          streamingStartTime,
          playbackUrl: null,
          ingestUrl: null,
          streamKey: null,
          channelArn,
          channelName,
          angle: cameraAngle,
          isAssigned: true,
          homeSchoolId: schoolId,
          opponentSchoolId,
          isPrimaryChannel: primaryUserId.toString() === userId,
          eventType,
          eventName,
          practiceName,
          seasonType,
          scheduleId,
          isPractice,
          seasonId,
          playlistName,
          playListId: playlistId,
          userId,
          weekName,
        });

        return;
      }

      // CASE 3: First streamer - Create new stage
      const createStageResponse = await userService.createStage(
        channelName,
        cameraAngle
      );
      const { stageArn, publishToken } = createStageResponse.data.data;

      setInToLocalStorage(
        "livestreamData",
        JSON.stringify({
          token: publishToken.token,
          stageArn,
          timestamp: Date.now(),
        })
      );

      joinStage(publishToken.token);

      const {
        uploadVideoEventType = "",
        practice = "",
        uploadvideoEvent = "",
        uploadVideoWeek = "",
        uploadVideoOffSeasonEventName = "",
        otherEventName = "",
        playlistData = [],
        uploadPlaylistName = "",
        uploadVideoWeekName = "",
      } = videoScheduleInformation;

      const eventName = uploadVideoOffSeasonEventName || otherEventName || "";

      const playlistId =
        playlistData.find((ele) => ele.playlistName === uploadPlaylistName)
          ?.id || null;

      const opponentSchoolId =
        videoScheduleInformation.opponentId ??
        videoStreamAvsChannelList[0]?.opponentSchoolId;

      addIVSStageDetails(dispatch, {
        streamingStartTime,
        playbackUrl: null,
        ingestUrl: null,
        streamKey: null,
        channelArn: stageArn,
        channelName,
        angle: cameraAngle,
        isAssigned: true,
        homeSchoolId: schoolId,
        opponentSchoolId,
        eventType: uploadVideoEventType,
        eventName,
        practiceName: practice,
        seasonType: uploadvideoEvent,
        scheduleId: uploadVideoWeek,
        isPractice: uploadvideoEvent === "Off-Season",
        seasonId: currentSeason?.id,
        playListId: playlistId,
        isPrimaryChannel: true,
        playlistName: uploadPlaylistName,
        userId,
        weekName: uploadVideoWeekName,
      });
    } catch (error) {
      setJoining(false);
      openFailDialog(dispatch, {
        open: true,
        message: "Something Went Wrong",
      });
    }
  };

  const leaveStage = async () => {
    setStopBtnLoading(true);

    const channelForDelete = videoStreamAvsChannelList.find(
      (ele) => userId === ele?.userId?.toString()
    );

    const cleanupStage = () => {
      if (stage) {
        stage.leave();
      }
      setStage(null);
      setConnected(false);
      setParticipants([]);
      removeFromLocalStorage("livestreamData");
    };

    const updateUIOnLeave = () => {
      setCurrentStageInformation(dispatch, {
        currentChannel: channelForDelete,
        channelStatus: false,
        videoStreamAvsChannelList,
        schoolId,
      });
    };

    const sendStopNotification = () => {
      userService.sendLiveStreamingNotification({
        id: userId,
        schoolId,
        associationType: "Stopped Stream",
      });
    };

    try {
      if (isPrimaryUser) {
        await userService.deleteStageRealTimeStream(
          channelForDelete?.channelArn
        );
      }

      cleanupStage();
      sendStopNotification();
      updateUIOnLeave();

      openSuccessPopup(dispatch, {
        successPopUp: {
          message: "Stream Stopped Successfully",
          open: true,
        },
      });
    } catch (error) {
      if (isPrimaryUser) {
        openFailDialog(dispatch, {
          open: true,
          message: "Failed To Delete Channel",
        });
      }
      updateUIOnLeave();
    } finally {
      setStopBtnLoading(false);
      setPage(0);
    }
  };

  const toggleMic = () => {
    if (micTrackRef.current) {
      const enabled = !micEnabled;
      micTrackRef.current.enabled = enabled;
      setMicEnabled(enabled);
    }
  };

  const toggleCamera = () => {
    if (cameraTrackRef.current) {
      const enabled = !cameraEnabled;
      cameraTrackRef.current.enabled = enabled;
      setCameraEnabled(enabled);
    }
  };

  return (
    <Container maxWidth="md" sx={{ mt: 4 }}>
      {videoStreamAvsChannelList.length != 0 && !connected && (
        <>
          {isPrimaryUser ? (
            <span className={`${styles.liveIcon}`}>
              <FaDotCircle className="red mg-right-5" />
              Channel is active. Click on "START STREAMING" to join the session
              or "STOP CHANNEL" to stop channel.
            </span>
          ) : (
            <span className={`${styles.liveIcon}`}>
              <FaDotCircle className="red mg-right-5" />
              Channel is active. Click on "START STREAMING" to join the session.
            </span>
          )}
          <div className="clear20" />
        </>
      )}
      <Box display="flex" flexDirection="column" gap={2} mb={3}>
        <PrimaryStreamer
          stageName={stageName}
          setStageName={setStageName}
          leaveStage={leaveStage}
          handleStartStreaming={handleStartStreaming}
          joining={joining}
          connected={connected}
          stopBtnLoading={stopBtnLoading}
        />
        <StreamAVController
          connected={connected}
          micEnabled={micEnabled}
          toggleMic={toggleMic}
          cameraEnabled={cameraEnabled}
          toggleCamera={toggleCamera}
        />
      </Box>
      <StreamingGridView participants={participants} />
    </Container>
  );
};

export default RealTimeStreaming;
